/**
 * Usage Monitoring Dashboard
 * 
 * Real-time monitoring of the centralized usage tracking system
 * Displays statistics, allows manual flushing, and shows system health
 */

import React, { useEffect, useState } from 'react'
import { 
  Activity, 
  Database, 
  RefreshCw, 
  Zap, 
  Users, 
  MessageSquare, 
  HardDrive,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { adminApi } from '../../services/admin/adminApi'
import { logger } from '../../utils/logger'

interface UsageStats {
  status: string
  batch_interval: number
  statistics: {
    organizations_tracked: number
    pending_events: number
    cache_size: number
    unique_visitors_tracked: number
    team_members_tracked: number
  }
  last_flush: number
}

interface UsageType {
  value: string
  description: string
}

export const UsageMonitoring: React.FC = () => {
  const [stats, setStats] = useState<UsageStats | null>(null)
  const [usageTypes, setUsageTypes] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [flushing, setFlushing] = useState(false)

  useEffect(() => {
    fetchStats()
    fetchUsageTypes()
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchStats, 10000)
    return () => clearInterval(interval)
  }, [])

  const fetchStats = async () => {
    try {
      const response = await adminApi.get('/usage-monitoring/stats')
      setStats(response.data)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch usage statistics')
      logger.error('Failed to fetch usage stats', { error: err })
    } finally {
      setLoading(false)
    }
  }

  const fetchUsageTypes = async () => {
    try {
      const response = await adminApi.get('/usage-monitoring/usage-types')
      setUsageTypes(response.data.descriptions || {})
    } catch (err: any) {
      logger.error('Failed to fetch usage types', { error: err })
    }
  }

  const handleForceFlush = async () => {
    setFlushing(true)
    try {
      await adminApi.get('/usage-monitoring/flush', {}, { method: 'POST' })
      // Refresh stats after flush
      await fetchStats()
      logger.info('Usage data flushed successfully')
    } catch (err: any) {
      setError(err.message || 'Failed to flush usage data')
      logger.error('Failed to flush usage data', { error: err })
    } finally {
      setFlushing(false)
    }
  }

  const formatLastFlush = (timestamp: number) => {
    const now = Date.now() / 1000
    const secondsAgo = Math.floor(now - timestamp)
    
    if (secondsAgo < 60) return `${secondsAgo}s ago`
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)}m ago`
    return `${Math.floor(secondsAgo / 3600)}h ago`
  }

  const getSystemStatus = () => {
    if (!stats) return { status: 'unknown', color: 'gray', icon: AlertCircle }
    
    const isHealthy = stats.status === 'active' && stats.statistics.pending_events < 1000
    return {
      status: isHealthy ? 'healthy' : 'warning',
      color: isHealthy ? 'green' : 'yellow',
      icon: isHealthy ? CheckCircle : AlertCircle
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading usage monitoring data...</p>
        </div>
      </div>
    )
  }

  const systemStatus = getSystemStatus()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Monitoring</h1>
          <p className="text-gray-600">Real-time monitoring of the centralized usage tracking system</p>
        </div>
        
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            onClick={fetchStats}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button 
            onClick={handleForceFlush}
            disabled={flushing}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Zap className={`mr-2 h-4 w-4 ${flushing ? 'animate-pulse' : ''}`} />
            {flushing ? 'Flushing...' : 'Force Flush'}
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <systemStatus.icon className={`h-6 w-6 text-${systemStatus.color}-600`} />
              <span className={`text-lg font-semibold text-${systemStatus.color}-600 capitalize`}>
                {systemStatus.status}
              </span>
            </div>
            
            {stats && (
              <>
                <div className="text-gray-400">•</div>
                <div className="text-sm text-gray-600">
                  Batch interval: {stats.batch_interval}s
                </div>
                <div className="text-gray-400">•</div>
                <div className="text-sm text-gray-600">
                  Last flush: {formatLastFlush(stats.last_flush)}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* Organizations Tracked */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Organizations</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.statistics.organizations_tracked}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Being tracked</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Events */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Events</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.statistics.pending_events}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">In queue</p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cache Size */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Cache Size</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.statistics.cache_size}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Cached lookups</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Database className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unique Visitors */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Visitors Tracked</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.statistics.unique_visitors_tracked}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This batch</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Team Members</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.statistics.team_members_tracked}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This batch</p>
                </div>
                <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Usage Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HardDrive className="h-5 w-5 mr-2" />
            Supported Usage Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(usageTypes).map(([type, description]) => (
              <div key={type} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 capitalize">
                      {type.replace('_', ' ')}
                    </p>
                    <p className="text-sm text-gray-600">{description}</p>
                  </div>
                  <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <Activity className="h-4 w-4 text-gray-600" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* System Configuration */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="h-5 w-5 mr-2" />
              System Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-600">Batch Interval</p>
                <p className="text-lg font-semibold text-gray-900">{stats.batch_interval} seconds</p>
                <p className="text-xs text-gray-500">How often data is flushed to database</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <p className="text-lg font-semibold text-gray-900 capitalize">{stats.status}</p>
                <p className="text-xs text-gray-500">Current tracking system status</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-600">Last Flush</p>
                <p className="text-lg font-semibold text-gray-900">{formatLastFlush(stats.last_flush)}</p>
                <p className="text-xs text-gray-500">When data was last written to database</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}